import React from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck } from "lucide-react";
import api from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { formatDistanceToNow } from "date-fns";

export default function NotificationsBell() {
  const [items, setItems] = React.useState([]);
  const [unread, setUnread] = React.useState(0);
  const nav = useNavigate();

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { page_size: 12 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {}
  }, []);

  React.useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const open = async (n) => { await api.post(`/notifications/${n.id}/read`); load(); if (n.link && n.link !== "/") nav(`/app${n.link.replace("/admin", "")}`); };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative h-10 w-10 rounded-lg border border-slate-200 bg-white flex items-center justify-center hover:border-slate-300 transition-colors" data-testid="notifications-bell">
          <Bell className="h-5 w-5 text-slate-600" />
          {unread > 0 && <span className="absolute -top-1 -right-1 h-5 min-w-[20px] rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center px-1">{unread > 9 ? "9+" : unread}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <span className="font-semibold text-sm text-slate-900">Notifications</span>
          {unread > 0 && <button onClick={markAll} className="text-xs text-vs-royal hover:underline flex items-center gap-1" data-testid="mark-all-read"><CheckCheck className="h-3.5 w-3.5" />Mark all read</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <div className="py-10 text-center text-sm text-slate-400">You're all caught up</div>}
          {items.map((n) => (
            <button key={n.id} onClick={() => open(n)} data-testid={`notification-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 flex gap-3 ${!n.read ? "bg-blue-50/40" : ""}`}>
              <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${n.read ? "bg-slate-200" : "bg-vs-royal"}`} />
              <div className="min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{n.title}</div>
                <div className="text-xs text-slate-500 line-clamp-2">{n.message}</div>
                <div className="text-[11px] text-slate-400 mt-1">{n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}</div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
